// ============================================================================
// UNIFIED TYPE DEFINITIONS FOR POKER GAME
// ============================================================================
// This file is the SINGLE SOURCE OF TRUTH for all game types.
// All services must import types from this file to ensure consistency.
// ============================================================================

// ===== CARD TYPES =====
// CRITICAL: Using '10' (not 'T') for consistency across all services

export type CardRank = '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';
export type CardSuit = 'hearts' | 'diamonds' | 'clubs' | 'spades';

export interface Card {
  rank: CardRank;
  suit: CardSuit;
}

// ===== HAND EVALUATION TYPES =====

export type HandRankType =
  | 'Royal Flush'
  | 'Straight Flush'
  | 'Four of a Kind'
  | 'Full House'
  | 'Flush'
  | 'Straight'
  | 'Three of a Kind'
  | 'Two Pair'
  | 'Pair'
  | 'High Card';

export interface Hand {
  cards: Card[];
  rank: HandRankType;
  highCards: Card[];
  tiebreakers: number[];
}

export interface EvaluatedHand {
  rank: HandRankType;
  value: number; // 1-10 (10 = Royal Flush, 1 = High Card)
  kickers: number[]; // Numeric values for tiebreaking
  bestFiveCards: Card[]; // Best 5-card combo from 7
}

// ===== PLAYER TYPES =====

export interface Player {
  id: string;
  chips: number;
  seat: number;
  holeCards: Card[];
  currentBet: number;
  totalBetThisRound: number;
  totalBetThisHand?: number; //  NEW: Track total bets for entire hand (survives round resets)

  folded: boolean;
  allIn: boolean;
  hasActedThisRound: boolean;
  lastAction?: PlayerAction;
  lastActionAmount?: number;
  eliminated: boolean;
  isDealer?: boolean;
  isSmallBlind?: boolean;
  isBigBlind?: boolean;
  lastActionTime?: number;
  evaluatedHand?: EvaluatedHand;
  buyIn?: number;
  name?: string; //  Add name for display

  //  Cash game status flags

  waitingForNextHand?: boolean;
  needsRebuy?: boolean;
  isReady?: boolean;
  photoURL?: string;
  isSittingOut?: boolean;  //  HEADS-UP FIX: Player busted but can rebuy

  isStraddle?: boolean;    //  SPECIAL RULES: Player posted a straddle this hand

  //  HOUSE BOT: Identifies this player as an automated house bot

  isHouseBot?: boolean;
  houseBotDifficulty?: 'easy' | 'medium' | 'hard';
}

export type PlayerAction = 'fold' | 'check' | 'call' | 'raise' | 'all-in' | 'bust';

// ===== BETTING & POT TYPES =====

export interface SidePot {
  amount: number;
  eligiblePlayers: string[]; // Player IDs eligible for this pot
  winners?: string[]; // Player IDs who won this side pot
}

export interface BettingRound {
  round: string;
  totalBet: number;
  playerActions: {
    [playerId: string]: PlayerAction;
  };
}

// ===== WINNER TYPE =====

export interface Winner {
  playerId: string;
  amount: number;
  hand?: HandRankType;
  handRank?: Hand | null;
}

// ===== BUST NOTIFICATION TYPE =====

export interface BustNotification {
  playerId: string;
  playerName: string;
  chipsBefore: number;
  tableId: string;
  handNumber: number;
  timestamp: Date;
}

// ===== HAND RESULT TYPE =====

//  SHOWDOWN SUSPENSE - Sequential card reveal state

export interface ShowdownState {
  isActive: boolean;                   // Is showdown in progress
  revealedPlayerIds: string[];         // Players whose cards have been revealed
  currentRevealIndex: number;          // Which player is currently being revealed
  revealOrder: string[];               // Order in which to reveal (worst to best hand)
  revealStartTime?: number;            // When the reveal sequence started
  allRevealed: boolean;                // Have all cards been shown
}

//  EQUITY/WIN PROBABILITY for suspense display

export interface PlayerEquity {
  playerId: string;
  equity: number;        // 0-100 win percentage
  handStrength?: string; // Current best hand description
}

export interface HandResult {
  winnerId: string;
  winnerName: string;
  winningHand: Hand | null;
  loserIds: string[];
  potWon: number;
  mainPot: number;
  sidePots: SidePot[];
  timestamp: number;
  round: string;
  //  For fold wins and show cards option

  allOthersFolded?: boolean;
  winnerShowedCards?: boolean;
}

// ===== GAME STATE TYPE =====

export type GameRound = 'preflop' | 'flop' | 'turn' | 'river' | 'showdown' | 'ended';
export type GameStatus = 'waiting' | 'ready' | 'playing' | 'paused' | 'ended';  //  Added 'ready' for auto-restart


export interface GameState {
  // Identifiers
  id?: string;
  tableId?: string;

  // Game status
  status: GameStatus;
  round: GameRound;

  // Players
  players: Player[];
  currentPlayerIndex: number;

  // Dealer & Blinds
  dealerButton: number; // UNIFIED: Using 'dealerButton' everywhere (not 'dealerIndex')
  smallBlindIndex?: number;
  bigBlindIndex?: number;
  smallBlind: number;
  bigBlind: number;

  // Pot & Cards
  pot: number;
  communityCards: Card[];
  deck: Card[];
  sidePots: SidePot[];

  // Current betting state
  currentBet?: number;
  totalBetThisRound?: number;
  lastRaiseAmount?: number; //  Track for min-raise rule


  // Hand tracking
  handNumber: number;
  handResult?: HandResult;
  winners?: Winner[];

  // Bust notifications for UI
  bustNotifications?: BustNotification[];

  // Timing
  timestamp?: number;
  bettingRoundComplete?: boolean;
  lastProgressTime?: number;

  // Timestamps
  createdAt?: Date;
  updatedAt?: Date;

  //  Currency for display

  currency?: 'playcoins' | 'pkcash';

  //  TURN TIMER (Standard 30 seconds)

  turnStartTime?: number;          // Timestamp when current player's turn started
  turnDuration?: number;           // Duration per turn in seconds (default: 30)
  
  //  SHOWDOWN SUSPENSE

  showdownState?: ShowdownState;   // Sequential card reveal state
  playerEquities?: PlayerEquity[]; // Win percentages for suspense display

  // ============================================================================
  // SPECIAL RULES STATE
  // ============================================================================

  // Special rules config (loaded from table)
  specialRules?: SpecialRules;

  //  FIX #5: Showdown idempotency guard

  showdownComplete?: boolean;

  // Bomb Pot state
  isBombPot?: boolean;
  bombPotAnte?: number;

  // Straddle state
  straddleAmount?: number;
  straddlerIndex?: number;

  //  FIX #3: 7-2 Bounty guard and result

  sevenTwoBountyPaid?: boolean;
  sevenTwoBountyResult?: {
    bountyAwarded: boolean;
    winnerId: string;
    totalBounty: number;
    bountyPerPlayer: number;
    payingPlayers: { playerId: string; amount: number }[];
    hand: string;
  };

  // Run It Twice state
  runItTwiceState?: {
    eligible: boolean;
    eligiblePlayers: string[];
    decisions: { [playerId: string]: boolean | null };
    runTwice: boolean;
    frozenMainPot?: number;
    frozenSidePots?: SidePot[];
    board1?: Card[];
    board2?: Card[];
    results?: {
      board1Winners: string[];
      board2Winners: string[];
      payouts: { playerId: string; totalAmount: number; boards: string[] }[];
    };
  };

  //  HOUSE BOT: Track if a house bot is active at this table

  houseBotActive?: boolean;
  houseBotId?: string;
  houseBotLeaving?: boolean; // True when bot is finishing its last hand before leaving
}

// ===== TABLE CONFIGURATION =====

export interface TableConfig {
  tableId: string;
  name: string;
  maxPlayers: number;
  smallBlind: number;
  bigBlind: number;
  buyIn: number;
  minBuyIn: number;
  maxBuyIn: number;
  minPlayers: number;
  currency: 'playcoins' | 'pkcash';
  rakePercent: number; // e.g., 0.05 for 5%
  rakeCap: number; // Maximum rake per hand
  createdAt: Date;
  updatedAt: Date;
}

// ===== HAND HISTORY TYPE =====

export interface HandHistory {
  handId: string;
  tableId: string;
  handNumber: number;
  timestamp: Date;
  players: {
    id: string;
    startChips: number;
    endChips: number;
    holeCards: Card[];
    folded: boolean;
    allIn: boolean;
    position: 'dealer' | 'sb' | 'bb' | 'utg' | 'mp' | 'co' | 'btn';
  }[];
  communityCards: Card[];
  pot: number;
  rake: number;
  winners: {
    playerId: string;
    amount: number;
    handType: HandRankType;
  }[];
  actions: {
    round: GameRound;
    playerId: string;
    action: PlayerAction;
    amount: number;
    timestamp: number;
  }[];
}

// ===== WALLET & TRANSACTION TYPES =====

export type TransactionType =
  | 'deposit'
  | 'withdrawal'
  | 'game_win'
  | 'game_loss'
  | 'rake_paid'
  | 'transfer'
  | 'bust'
  | 'referral_bonus'
  | 'purchase';

export interface WalletTransaction {
  id: string;
  userId: string;
  type: TransactionType;
  currency: 'playcoins' | 'pkcash';
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string;
  handId?: string;
  tableId?: string;
  createdAt: Date;
  status: 'pending' | 'completed' | 'failed';
}

export interface PlayerWallet {
  userId: string;
  playCoins: number;
  pkCash: number;
  pkCashPlaythrough: number; // For playthrough requirements
  totalDeposits: number;
  totalWithdrawals: number;
  totalWinnings: number;
  totalLosses: number;
  totalRakePaid: number;
  transactionHistory: WalletTransaction[];
  createdAt: Date;
  updatedAt: Date;
}

// ===== PLAYER STATS TYPES =====

export interface PlayerStats {
  userId: string;
  userName: string;
  totalHands: number;
  handsWon: number;
  handsLost: number;
  winRate: number; // Percentage
  totalWinnings: number;
  totalLosses: number;
  profitLoss: number; // Winnings - Losses
  largestPot: number;
  averagePotWon: number;
  vpip: number; // Voluntarily Put $ In Pot %
  pfr: number; // Pre-Flop Raise %
  aggression: number; // Aggression Factor
  showdownWinRate: number;
  allInWinRate: number;
}

// ===== API TYPES =====

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface GameActionRequest {
  action: PlayerAction;
  raiseAmount?: number;
}

export interface GameActionResponse {
  success: boolean;
  newGameState: GameState;
  message: string;
}

// ===== UTILITY: RANK VALUE MAPPING =====
// Use this constant for consistent rank-to-value conversion across all services

export const RANK_VALUES: { [key in CardRank]: number } = {
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 10,
  'J': 11,
  'Q': 12,
  'K': 13,
  'A': 14,
};

// ===== UTILITY: HAND RANK VALUES =====
// Higher number = better hand

export const HAND_RANK_VALUES: { [key in HandRankType]: number } = {
  'High Card': 1,
  'Pair': 2,
  'Two Pair': 3,
  'Three of a Kind': 4,
  'Straight': 5,
  'Flush': 6,
  'Full House': 7,
  'Four of a Kind': 8,
  'Straight Flush': 9,
  'Royal Flush': 10,
};


// ============================================================================
// ============================================================================
//  NEW TYPES FOR ENHANCED TABLE CREATION (Added below - does not affect existing code)

// ============================================================================
// ============================================================================

/**
 * Game Variant - Type of poker being played
 * Currently only Texas Hold'em, structured for future expansion
 */
export type GameVariant = 'texas_holdem'; // Future: 'omaha' | 'pineapple' | 'short_deck'

/**
 * Betting Structure - How bets are limited
 * Currently only No Limit, structured for future expansion
 */
export type BettingStructure = 'no_limit'; // Future: 'pot_limit' | 'fixed_limit'

/**
 * Special Rules Configuration
 * These are optional game modes set at table creation - CANNOT be changed during game
 */
export interface SpecialRules {
  // ===== 7-2 Bounty Game =====
  // If a player wins a pot holding 7-2 offsuit, they collect a bounty from each player
  sevenTwoGame: boolean;
  sevenTwoBounty: number; // Bounty amount in BB multiples (e.g., 2 = 2x Big Blind per player)

  // ===== Bomb Pot =====
  // Everyone antes extra, skip preflop betting, go straight to flop
  // Host triggers manually via table options menu
  bombPotEnabled: boolean;
  bombPotAnte: number; // Ante per player in BB multiples (e.g., 2 = 2x Big Blind)

  // ===== Straddle =====
  // UTG can optionally post a blind bet (2x-4x BB) before cards are dealt
  straddleEnabled: boolean;
  straddleMin: number; // Minimum straddle in BB multiples (typically 2)
  straddleMax: number; // Maximum straddle in BB multiples (typically 4)

  // ===== Run It Twice =====
  // When all-in before river, either player can request, other must accept
  // Deal remaining cards twice, split pot if different winners
  runItTwiceEnabled: boolean;

  // ===== All-In or Fold =====
  // Simplified game mode: only two actions available. Everyone sits with a
  // fixed short stack (aofStackBB big blinds); after each hand the server
  // tops a short stack back up from the wallet and banks any excess.
  allInOrFold: boolean;
  aofStackBB?: number;
}

/**
 * Default special rules - all disabled
 * Used when creating tables without special rules
 */
export const DEFAULT_SPECIAL_RULES: SpecialRules = {
  sevenTwoGame: false,
  sevenTwoBounty: 2, // 2x BB default if enabled
  bombPotEnabled: false,
  bombPotAnte: 2, // 2x BB default if enabled
  straddleEnabled: false,
  straddleMin: 2,
  straddleMax: 4,
  runItTwiceEnabled: false,
  allInOrFold: false,
  aofStackBB: 8,
};

/**
 * Extended Table Data - Full table configuration including special rules
 * This is the complete Firestore document structure for enhanced tables
 * 
 * BACKWARD COMPATIBLE: Old tables without these fields will still work
 * - Missing fields default to standard values
 * - isPrivate defaults to false
 * - specialRules defaults to DEFAULT_SPECIAL_RULES
 */
export interface ExtendedTableData {
  // ===== Core Identity =====
  id: string;
  name: string; // Auto-generated or custom
  customName?: string; // User's custom name (if provided)
  ownerId: string;

  // ===== Game Configuration =====
  gameVariant: GameVariant;
  bettingStructure: BettingStructure;
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
  maxSeats: number; // 2-10

  // ===== Currency =====
  currency: 'playcoins' | 'pkcash';

  // ===== Privacy =====
  isPrivate: boolean;
  privateCode?: string; // 6-digit code for private tables

  // =====  Voice Chat =====

  voiceChatEnabled?: boolean; // Whether voice chat is active for this table
  voiceChatParticipants?: number; // Current number of players in voice channel

  // ===== Special Rules (Set at creation, cannot change during game) =====
  specialRules: SpecialRules;

  // ===== Players =====
  players: string[]; // Array of player UIDs
  playerBuyIns: { [playerId: string]: number };
  readyPlayers: string[];

  // ===== Game State =====
  status: 'waiting' | 'playing' | 'finished';
  pot: number;
  gameState?: GameState;

  // ===== Timestamps =====
  createdAt: any; // Firestore Timestamp
  updatedAt: any; // Firestore Timestamp

  // ===== Tags (for filtering/search) =====
  tags?: string[];
}

/**
 * Table Creation Request - Data needed to create a new enhanced table
 * Used by CreateTablePage
 */
export interface CreateTableRequest {
  // Required fields
  ownerId: string;
  currency: 'playcoins' | 'pkcash';
  smallBlind: number;
  bigBlind: number;

  // Optional with defaults
  customName?: string; // If not provided, auto-generate
  maxSeats?: number; // Default: 8
  minBuyIn?: number; // Default: 20x BB
  maxBuyIn?: number; // Default: 100x BB
  isPrivate?: boolean; // Default: false
  privateCode?: string; // Auto-generate if private and not provided
  specialRules?: Partial<SpecialRules>; // Merge with defaults
  
  //  Voice Chat

  voiceChatEnabled?: boolean; // Default: false
}

/**
 * Join Private Table Request
 */
export interface JoinPrivateTableRequest {
  tableId: string;
  playerId: string;
  privateCode: string;
}

/**
 * Hand Ranking Info - For displaying hand rankings in rules modal
 */
export interface HandRankingInfo {
  rank: number; // 1-10, 10 being best
  name: HandRankType;
  description: string;
  example: string;
  probability: string; // e.g., "0.000154%"
}

/**
 * Complete hand rankings data for rules modal
 */
export const HAND_RANKINGS_INFO: HandRankingInfo[] = [
  {
    rank: 10,
    name: 'Royal Flush',
    description: 'A, K, Q, J, 10 all of the same suit',
    example: 'A K Q J 10',

    probability: '0.000154%',
  },
  {
    rank: 9,
    name: 'Straight Flush',
    description: 'Five cards in sequence, all of the same suit',
    example: '9 8 7 6 5',

    probability: '0.00139%',
  },
  {
    rank: 8,
    name: 'Four of a Kind',
    description: 'Four cards of the same rank',
    example: 'K K K K 2',

    probability: '0.0240%',
  },
  {
    rank: 7,
    name: 'Full House',
    description: 'Three of a kind plus a pair',
    example: 'J J J 8 8',

    probability: '0.144%',
  },
  {
    rank: 6,
    name: 'Flush',
    description: 'Five cards of the same suit, not in sequence',
    example: 'A J 8 6 2',

    probability: '0.197%',
  },
  {
    rank: 5,
    name: 'Straight',
    description: 'Five cards in sequence, not all of the same suit',
    example: '10 9 8 7 6',

    probability: '0.392%',
  },
  {
    rank: 4,
    name: 'Three of a Kind',
    description: 'Three cards of the same rank',
    example: 'Q Q Q 9 4',

    probability: '2.11%',
  },
  {
    rank: 3,
    name: 'Two Pair',
    description: 'Two different pairs',
    example: 'A A 7 7 3',

    probability: '4.75%',
  },
  {
    rank: 2,
    name: 'Pair',
    description: 'Two cards of the same rank',
    example: '10 10 K 5 2',

    probability: '42.3%',
  },
  {
    rank: 1,
    name: 'High Card',
    description: 'No matching cards - highest card plays',
    example: 'A J 8 5 2',

    probability: '50.1%',
  },
];

/**
 * Special Rule Info - For displaying rule explanations in UI
 */
export interface SpecialRuleInfo {
  id: keyof SpecialRules;
  name: string;
  shortName: string;
  icon: string;
  description: string;
  detailedExplanation: string;
}

/**
 * Special rules information for tooltips and modals
 */
export const SPECIAL_RULES_INFO: SpecialRuleInfo[] = [
  {
    id: 'sevenTwoGame',
    name: '7-2 Bounty',
    shortName: '7-2',
    icon: '',

    description: 'Win with 7-2 offsuit and collect a bounty from each player!',
    detailedExplanation: 
      'The 7-2 Game adds extra excitement! If you win a pot while holding 7-2 offsuit ' +
      '(the statistically worst starting hand in poker), each player at the table who saw ' +
      'the flop pays you a bounty. The bounty amount is set when the table is created ' +
      '(typically 2x the Big Blind per player). This encourages creative play and bluffing!',
  },
  {
    id: 'bombPotEnabled',
    name: 'Bomb Pot',
    shortName: 'Bomb',
    icon: '',

    description: 'Everyone antes, skip preflop, and see the flop immediately!',
    detailedExplanation: 
      'A Bomb Pot is an exciting variation where everyone posts an ante (typically 2x Big Blind), ' +
      'preflop betting is skipped entirely, and the flop is dealt immediately. This creates ' +
      'bigger pots and more action! The table host can trigger a Bomb Pot between hands ' +
      'using the table options menu.',
  },
  {
    id: 'straddleEnabled',
    name: 'Straddle',
    shortName: 'Straddle',
    icon: '⬆',

    description: 'UTG can post a voluntary blind bet before cards are dealt.',
    detailedExplanation: 
      'A Straddle is a voluntary blind bet posted by the player Under The Gun (UTG) before ' +
      'cards are dealt. The straddle amount is typically 2x the Big Blind (but can be up to 4x ' +
      'at some tables). When someone straddles, they get to act last preflop, and the minimum ' +
      'raise is based on the straddle amount. This creates bigger pots and more action!',
  },
  {
    id: 'runItTwiceEnabled',
    name: 'Run It Twice',
    shortName: 'Run 2x',
    icon: '',

    description: 'When all-in, deal remaining cards twice and split the pot.',
    detailedExplanation: 
      'Run It Twice is an option available when players are all-in before the river. ' +
      'Either player can request to run it twice. If both agree, the remaining community ' +
      'cards are dealt twice, creating two separate boards. If the same player wins both ' +
      'runs, they take the whole pot. If different players win each run, the pot is split. ' +
      'This reduces variance in big all-in situations.',
  },
  {
    id: 'allInOrFold',
    name: 'All-In or Fold',
    shortName: 'AoF',
    icon: '',

    description: 'Two buttons: all-in or fold. Fixed 8 big blind stacks, topped up every hand.',
    detailedExplanation:
      'All-In or Fold (AoF) is a fast-paced poker variant where players can only go all-in ' +
      'or fold - no calling, checking, or raising. Everyone sits with exactly 8 big blinds. ' +
      'After each hand a short stack is topped back up from your wallet and anything above ' +
      '8 big blinds is banked to your wallet, so every hand starts even. Quick, high-action ' +
      'games where every decision is for all your chips.',
  },
];

/**
 * Get active special rules from a table's configuration
 * Returns array of active rule info objects for display
 */
export function getActiveSpecialRules(specialRules: SpecialRules | undefined): SpecialRuleInfo[] {
  if (!specialRules) return [];
  
  const activeRules: SpecialRuleInfo[] = [];
  
  if (specialRules.sevenTwoGame) {
    activeRules.push(SPECIAL_RULES_INFO.find(r => r.id === 'sevenTwoGame')!);
  }
  if (specialRules.bombPotEnabled) {
    activeRules.push(SPECIAL_RULES_INFO.find(r => r.id === 'bombPotEnabled')!);
  }
  if (specialRules.straddleEnabled) {
    activeRules.push(SPECIAL_RULES_INFO.find(r => r.id === 'straddleEnabled')!);
  }
  if (specialRules.runItTwiceEnabled) {
    activeRules.push(SPECIAL_RULES_INFO.find(r => r.id === 'runItTwiceEnabled')!);
  }
  if (specialRules.allInOrFold) {
    activeRules.push(SPECIAL_RULES_INFO.find(r => r.id === 'allInOrFold')!);
  }
  
  return activeRules;
}

/**
 * Generate a 6-digit private table code
 */
export function generatePrivateCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Check if a table has any special rules enabled
 */
export function hasSpecialRules(specialRules: SpecialRules | undefined): boolean {
  if (!specialRules) return false;
  return (
    specialRules.sevenTwoGame ||
    specialRules.bombPotEnabled ||
    specialRules.straddleEnabled ||
    specialRules.runItTwiceEnabled ||
    specialRules.allInOrFold
  );
}